// @flow
import React from 'react';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import Figure from './Figure.js';
import {
  FIGURE_COLUMNS,
  useImageSizeValidation,
  useValidImageWidthValidation,
} from './Image.js';
import { getPixelWidthFromColumns, RowContext } from './grid.js';

const DraftImage = (props: {|
  id: string,
  url: string,
  notesForImageCreator: string,
  width: number,
  height: number,
  // There are certain cases where we don't want <Figure> to
  // apply its default margins. At time of writing, SolidifyFigureGrid
  // and SolidifyAnnotatedFigure both require this. I'm not really happy
  // with this approach though, so need to rethink it sometime.
  _removeFigureMargin?: boolean,
  ...CompilerInjectedProps,
|}) => {
  const largestValidFigureSize = useLargestValidFigureSize();
  const width = props.width || largestValidFigureSize;
  const { failedValidation } = usePropsValidation(props, DraftImage);

  useValidImageWidthValidation({ ...props, width }, DraftImage);
  useImageSizeValidation({ ...props, width }, DraftImage);

  if (failedValidation) return null;
  const { url, height } = props;
  return (
    <Figure _removeFigureMargin={props._removeFigureMargin}>
      <img src={url} width={width} height={height} />
    </Figure>
  );
};
DraftImage.spec = {
  id: t.uuid,
  url: urlChecker,
  notesForImageCreator: nonEmptyStringChecker,
  // width and height are now required, but we have maintained the old
  // default logic so current content does not break.
  // We will unwind it when existing content has been updated to
  // reflect the new validation.
  width: t.number,
  height: t.number,
};

function urlChecker(props, propName) {
  const value = props[propName];
  try {
    if (typeof value !== 'string') throw Error('must be a string');
    // This will throw if a malformed URL is passed (eg. only providing a path, not a fully-qualified URL)
    const url = new URL(value);
    if (url.protocol !== 'https:') {
      return {
        typeDescription: urlChecker.description,
        reason: `must use a https:// URL, not ${url.protocol}`,
      };
    }
  } catch (error) {
    return {
      typeDescription: urlChecker.description,
      reason: 'must be a valid URL. Eg. https://blah.com/image.png',
    };
  }

  return null;
}
urlChecker.description = 'URL';

function nonEmptyStringChecker(props, propName) {
  const value = props[propName];

  const failure = t.string(props, propName);
  if (failure !== null) return failure;
  // Refinement for Flow as it doesn't know t.string enforces a string type
  if (typeof value !== 'string') {
    throw Error('runtime typecheck invariant failure');
  }

  if (value.trim().length === 0) {
    return {
      typeDescription: nonEmptyStringChecker.description,
      reason: 'must be a string with non-whitespace content in it.',
    };
  }

  return null;
}
nonEmptyStringChecker.description = 'NonEmptyString';

export default DraftImage;

function useLargestValidFigureSize(): number {
  const { availableColumns } = React.useContext(RowContext);
  const largestValidFigureSizeInColumns = FIGURE_COLUMNS.find(
    (column) => column <= availableColumns,
  );
  // NOTE Probably need a better way to handle this condition
  if (largestValidFigureSizeInColumns === undefined) return 0;
  return getPixelWidthFromColumns(largestValidFigureSizeInColumns);
}
